import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { 
  Building2, 
  Plus, 
  Edit2, 
  Trash2, 
  Search,
  Globe,
  Users,
  DollarSign,
  MoreVertical,
  X,
  Star,
  Package,
  Check
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';

const CompetitorManagement = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [partTypeSearch, setPartTypeSearch] = useState('');
  const [showPartTypeDropdown, setShowPartTypeDropdown] = useState(false);
  const partTypeInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    description: '',
    headquarters_country: '',
    website_url: '',
    industry_segments: [],
    annual_revenue_millions: '',
    employee_count: '',
    is_my_company: false
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          partTypeInputRef.current && !partTypeInputRef.current.contains(event.target)) {
        setShowPartTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch competitors
  const { data: competitors = [], isLoading } = useQuery({
    queryKey: ['competitors', searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      const response = await apiClient.get(`/api/competitive-intelligence/competitors?${params}`);
      return response.data;
    }
  });

  // Fetch part types for the checkbox selection
  const { data: partTypes = [] } = useQuery({
    queryKey: ['partTypes'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/part-types');
      return response.data;
    }
  });

  // Create competitor mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiClient.post('/api/competitive-intelligence/competitors', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['competitors']);
      setIsCreateDialogOpen(false);
      resetForm();
    }
  });

  // Update competitor mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`/api/competitive-intelligence/competitors/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['competitors']);
      setEditingCompetitor(null);
      resetForm();
    }
  });

  // Delete competitor mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`/api/competitive-intelligence/competitors/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['competitors']);
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      short_name: '',
      description: '',
      headquarters_country: '',
      website_url: '',
      industry_segments: [],
      annual_revenue_millions: '',
      employee_count: '',
      is_my_company: false
    });
  };

  const handleEdit = (competitor) => {
    setEditingCompetitor(competitor);
    setFormData({
      name: competitor.name || '',
      short_name: competitor.short_name || '',
      description: competitor.description || '',
      headquarters_country: competitor.headquarters_country || '',
      website_url: competitor.website_url || '',
      industry_segments: competitor.industry_segments || [],
      annual_revenue_millions: competitor.annual_revenue_millions || '',
      employee_count: competitor.employee_count || '',
      is_my_company: competitor.is_my_company || false
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      annual_revenue_millions: formData.annual_revenue_millions ? parseInt(formData.annual_revenue_millions) : null,
      employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
      is_my_company: formData.is_my_company
    };

    if (editingCompetitor) {
      updateMutation.mutate({ id: editingCompetitor.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  // Toggle a part type selection
  const addPartType = (partTypeName) => {
    if (!formData.industry_segments.includes(partTypeName)) {
      setFormData(prev => ({
        ...prev,
        industry_segments: [...prev.industry_segments, partTypeName]
      }));
    }
    setPartTypeSearch('');
    setShowPartTypeDropdown(false);
  };

  const removePartType = (partTypeName) => {
    setFormData(prev => ({
      ...prev,
      industry_segments: prev.industry_segments.filter(s => s !== partTypeName)
    }));
  };

  // Filter part types based on search - exclude already selected
  const filteredPartTypes = partTypes.filter(pt => {
    const matchesSearch = pt.name.toLowerCase().includes(partTypeSearch.toLowerCase()) ||
                          (pt.code && pt.code.toLowerCase().includes(partTypeSearch.toLowerCase()));
    const notAlreadySelected = !formData.industry_segments.includes(pt.name);
    return matchesSearch && notAlreadySelected;
  });

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingCompetitor(null);
    setPartTypeSearch('');
    setShowPartTypeDropdown(false);
    resetForm();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Competitor Registry</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage your list of competitors for market share analysis
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Competitor
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search competitors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Competitor Cards */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-500">Loading competitors...</div>
      ) : competitors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No competitors yet</h3>
            <p className="text-slate-500 mb-4">Add your first competitor to start tracking market share</p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Competitor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {competitors.map((competitor) => (
            <Card key={competitor.id} className={`hover:shadow-md transition-shadow ${competitor.is_my_company ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${competitor.is_my_company ? 'bg-blue-100' : 'bg-slate-100'}`}>
                      {competitor.is_my_company ? (
                        <Star className="h-5 w-5 text-blue-600 fill-blue-600" />
                      ) : (
                        <Building2 className="h-5 w-5 text-slate-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base">{competitor.name}</CardTitle>
                        {competitor.is_my_company && (
                          <Badge className="bg-blue-600 text-white text-xs">My Company</Badge>
                        )}
                      </div>
                      {competitor.short_name && (
                        <p className="text-xs text-slate-500">{competitor.short_name}</p>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(competitor)}>
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => deleteMutation.mutate(competitor.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {competitor.description && (
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{competitor.description}</p>
                )}
                
                <div className="space-y-2 text-sm">
                  {competitor.headquarters_country && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Globe className="h-4 w-4 text-slate-400" />
                      {competitor.headquarters_country}
                    </div>
                  )}
                  {competitor.annual_revenue_millions && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <DollarSign className="h-4 w-4 text-slate-400" />
                      ${competitor.annual_revenue_millions.toLocaleString()}M revenue
                    </div>
                  )}
                  {competitor.employee_count && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="h-4 w-4 text-slate-400" />
                      {competitor.employee_count.toLocaleString()} employees
                    </div>
                  )}
                </div>

                {competitor.industry_segments && competitor.industry_segments.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      Part Types
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {competitor.industry_segments.slice(0, 3).map((segment) => (
                        <Badge key={segment} className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                          {segment}
                        </Badge>
                      ))}
                      {competitor.industry_segments.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{competitor.industry_segments.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 mt-4 pt-3 border-t text-xs text-slate-500">
                  <span>{competitor.segment_rules_count} segment rules</span>
                  <span>{competitor.carline_overrides_count} overrides</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen || !!editingCompetitor} onOpenChange={(open) => {
        if (!open) handleCloseDialog();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCompetitor ? 'Edit Competitor' : 'Add Competitor'}
            </DialogTitle>
            <DialogDescription>
              {editingCompetitor 
                ? 'Update competitor information'
                : 'Add a new competitor to track in your market analysis'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Autoliv"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="short_name">Short Name / Ticker</Label>
                <Input
                  id="short_name"
                  value={formData.short_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, short_name: e.target.value }))}
                  placeholder="e.g., ALV"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the competitor..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="headquarters_country">Headquarters Country</Label>
                <Input
                  id="headquarters_country"
                  value={formData.headquarters_country}
                  onChange={(e) => setFormData(prev => ({ ...prev, headquarters_country: e.target.value }))}
                  placeholder="e.g., Sweden"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website_url">Website</Label>
                <Input
                  id="website_url"
                  value={formData.website_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="annual_revenue_millions">Annual Revenue ($ Millions)</Label>
                <Input
                  id="annual_revenue_millions"
                  type="number"
                  value={formData.annual_revenue_millions}
                  onChange={(e) => setFormData(prev => ({ ...prev, annual_revenue_millions: e.target.value }))}
                  placeholder="e.g., 8500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="employee_count">Employees</Label>
                <Input
                  id="employee_count"
                  type="number"
                  value={formData.employee_count}
                  onChange={(e) => setFormData(prev => ({ ...prev, employee_count: e.target.value }))}
                  placeholder="e.g., 60000"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-500" />
                Part Types / Product Segments
              </Label>
              <p className="text-xs text-slate-500 mb-2">
                Search and select the part types this competitor supplies
              </p>
              
              {/* Selected Part Types as Tags */}
              {formData.industry_segments.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {formData.industry_segments.map((segment) => (
                    <Badge 
                      key={segment} 
                      className="bg-blue-100 text-blue-800 hover:bg-blue-200 pl-2.5 pr-1 py-1"
                    >
                      {segment}
                      <button
                        type="button"
                        onClick={() => removePartType(segment)}
                        className="ml-1.5 p-0.5 rounded-full hover:bg-blue-300 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Typeahead Search Input */}
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    ref={partTypeInputRef}
                    placeholder="Type to search part types..."
                    value={partTypeSearch}
                    onChange={(e) => {
                      setPartTypeSearch(e.target.value);
                      setShowPartTypeDropdown(true);
                    }}
                    onFocus={() => setShowPartTypeDropdown(true)}
                    className="pl-9"
                  />
                </div>
                
                {/* Dropdown Suggestions */}
                {showPartTypeDropdown && (
                  <div 
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  >
                    {partTypes.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-slate-400 text-center">
                        No part types defined yet
                      </div>
                    ) : filteredPartTypes.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-slate-400 text-center">
                        {partTypeSearch ? 'No matching part types' : 'All part types selected'}
                      </div>
                    ) : (
                      filteredPartTypes.map((pt) => (
                        <button
                          key={pt.id}
                          type="button"
                          onClick={() => addPartType(pt.name)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                        >
                          <Plus className="h-4 w-4 text-slate-400" />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-slate-900">
                              {pt.name}
                            </span>
                            {pt.code && (
                              <span className="ml-2 text-xs text-slate-400">({pt.code})</span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-2 pb-2 border-t border-b bg-blue-50/50 px-3 py-3 rounded-md">
              <Checkbox
                id="is_my_company"
                checked={formData.is_my_company}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_my_company: checked }))}
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="is_my_company"
                  className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                >
                  <Star className="h-4 w-4 text-blue-600" />
                  This is my company
                </Label>
                <p className="text-xs text-slate-500">
                  Mark this to identify your company in market share analysis. Only one company can be marked.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingCompetitor ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompetitorManagement;
